import { useState } from 'react';
import { FormInstance, FormProps, message } from 'antd';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from './const';
import { fetchAllPages } from './fetchAllPages';

type PageResponse<Row> = { meta: { last_page: number }; data: Row[] };

type Params<FormValues, Filters, Row> = {
    form: FormInstance<FormValues>;
    emptyFilters: Filters;
    toAppliedFilters: (values: FormValues) => Filters;
    fetchPage: (
        filters: Filters,
        page: number,
        perPage: number,
    ) => Promise<PageResponse<Row>>;
    exportRows: (rows: Row[]) => Promise<unknown>;
    exportErrorMessage: string;
};

export function useListTab<FormValues, Filters, Row>({
    form,
    emptyFilters,
    toAppliedFilters,
    fetchPage,
    exportRows,
    exportErrorMessage,
}: Params<FormValues, Filters, Row>) {
    const [filters, setFilters] = useState<Filters>(emptyFilters);
    const [isExporting, setIsExporting] = useState(false);
    const [pagination, setPagination] = useState({
        page: DEFAULT_PAGE,
        perPage: DEFAULT_PER_PAGE,
    });

    const onSearch: FormProps<FormValues>['onFinish'] = (values) => {
        setFilters(toAppliedFilters(values));
        setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    };

    const onReset = () => {
        form.resetFields();
        setFilters(emptyFilters);
        setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    };

    const onPageChange = (page: number, perPage: number) =>
        setPagination({ page, perPage });

    const onExport = async () => {
        setIsExporting(true);
        try {
            const rows = await fetchAllPages((page, perPage) =>
                fetchPage(filters, page, perPage),
            );
            if (!rows.length) {
                message.error('Nenhum dado disponível para ser exportado!');
                return;
            }
            await exportRows(rows);
        } catch (error) {
            console.error(error);
            message.error(exportErrorMessage);
        } finally {
            setIsExporting(false);
        }
    };

    return {
        filters,
        pagination,
        onSearch,
        onReset,
        onPageChange,
        onExport,
        isExporting,
    };
}
